import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { computeMaintStatus, STATUS_COLORS, fmtDate, fmtNum } from "../lib/status";
import { Truck, AlertTriangle, Package, Activity, ArrowRight, Wrench } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [fleet, setFleet] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [intervals, setIntervals] = useState([]);
  const [audit, setAudit] = useState([]);
  const [services, setServices] = useState([]);

  const load = async () => {
    const calls = [
      api.get("/fleet"),
      api.get("/inventory"),
      api.get("/intervals"),
      api.get("/service-logs/recent?limit=15").catch(() => ({ data: [] })),
    ];
    if (isAdmin) calls.push(api.get("/audit?limit=15").catch(() => ({ data: [] })));
    const [f, i, iv, s, a] = await Promise.all(calls);
    setFleet(f.data); setInventory(i.data); setIntervals(iv.data);
    setServices(s.data);
    setAudit(a ? a.data : []);
  };

  useEffect(() => { load(); const id = setInterval(load, 6000); return () => clearInterval(id); /* eslint-disable-next-line */ }, [isAdmin]);

  const overdue = useMemo(() => fleet.filter((u) => computeMaintStatus(u, intervals).key === "overdue"), [fleet, intervals]);
  const dueSoon = useMemo(() => fleet.filter((u) => computeMaintStatus(u, intervals).key === "due_soon"), [fleet, intervals]);
  const needsData = useMemo(() => fleet.filter((u) => computeMaintStatus(u, intervals).key === "needs_data"), [fleet, intervals]);
  const lowStock = useMemo(() => inventory.filter((it) => (it.qty ?? 0) <= (it.reorder ?? 0)), [inventory]);

  const stats = [
    { label: "Active Units", val: fleet.filter((u) => !u.archived).length, icon: Truck, tone: "text-zinc-100", to: "/fleet" },
    { label: "Overdue", val: overdue.length, icon: AlertTriangle, tone: "text-red-400", to: "/maintenance" },
    { label: "Due Soon", val: dueSoon.length, icon: AlertTriangle, tone: "text-amber-400", to: "/maintenance" },
    { label: "Low Stock", val: lowStock.length, icon: Package, tone: "text-red-400", to: "/inventory" },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Shop Overview</h1>
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mt-1">Live · Polls every 6s</p>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((s) => (
          <Link to={s.to} key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}
            className="border border-zinc-800 bg-zinc-900 hover:bg-zinc-800/80 hover:border-zinc-700 p-4 rounded-sm transition-colors group">
            <div className="flex items-start justify-between">
              <s.icon className={`h-5 w-5 ${s.tone}`} />
              <ArrowRight className="h-4 w-4 text-zinc-600 group-hover:text-zinc-300 transition-colors" />
            </div>
            <div className={`mt-3 text-3xl font-mono font-bold ${s.tone}`}>{s.val}</div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mt-1">{s.label}</div>
          </Link>
        ))}
      </div>

      {/* Attention lists */}
      <div className="grid lg:grid-cols-2 gap-4">
        <AttentionList title="Overdue Maintenance" items={overdue} intervals={intervals} accent="overdue" empty="Nothing overdue" />
        <AttentionList title="Due Soon" items={dueSoon} intervals={intervals} accent="due_soon" empty="Nothing due soon" />
      </div>

      <RecentServiceList services={services} />

      {isAdmin && (
        <div className="border border-zinc-800 bg-zinc-900 rounded-sm">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
            <Activity className="h-4 w-4 text-zinc-400" />
            <h3 className="text-sm font-semibold">Recent Activity</h3>
          </div>
          <ul className="divide-y divide-zinc-800 max-h-[380px] overflow-y-auto" data-testid="recent-activity">
            {audit.length === 0 && <li className="p-4 text-sm text-zinc-500">No activity yet.</li>}
            {audit.map((a) => (
              <li key={a.id} className="px-4 py-2.5 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-zinc-100 font-medium">{a.username}</span>
                  <span className="text-zinc-500">·</span>
                  <span className="text-zinc-400 font-mono text-xs uppercase">{a.action}</span>
                </div>
                {a.summary && <div className="text-xs text-zinc-500 mt-0.5">{a.summary}</div>}
                <div className="text-[10px] font-mono text-zinc-600 mt-0.5">{fmtDate(a.timestamp)}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {needsData.length > 0 && (
        <div className="border border-red-500/30 bg-red-500/5 p-4 rounded-sm" data-testid="needs-data-alert">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-2 h-2 rounded-full bg-red-400 pulse-dot" />
            <span className="text-sm font-semibold text-red-300">{needsData.length} units need data</span>
          </div>
          <div className="text-xs text-zinc-400">Enter mileage or hours to compute service status.</div>
        </div>
      )}
    </div>
  );
}

function fmtMoney(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  if (Number.isNaN(n)) return null;
  return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
}

function RecentServiceList({ services }) {
  const label = (k) => {
    if (k === "annual_inspection") return "Annual Insp.";
    if (k === "repair") return "Repair";
    if (k === "maintenance") return "Maintenance";
    return "Service";
  };
  return (
    <div className="border border-zinc-800 bg-zinc-900 rounded-sm" data-testid="recent-service-panel">
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
        <Wrench className="h-4 w-4 text-red-400" />
        <h3 className="text-sm font-semibold">Recent Service / Repair / Maintenance</h3>
      </div>
      <ul className="divide-y divide-zinc-800 max-h-[420px] overflow-y-auto" data-testid="recent-service-list">
        {services.length === 0 && <li className="p-4 text-sm text-zinc-500">No service logs yet.</li>}
        {services.map((s) => {
          const parts = fmtMoney(s.parts_cost);
          const labor = fmtMoney(s.labor_cost);
          const total = fmtMoney(s.total_cost ?? ((s.parts_cost || 0) + (s.labor_cost || 0) || null));
          return (
            <li key={s.id} className="px-4 py-3" data-testid={`recent-service-${s.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/fleet/${s.unit_id}`} className="font-mono font-bold text-zinc-50 hover:text-red-300 transition-colors" data-testid={`recent-service-unit-${s.id}`}>{s.unit}</Link>
                    <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border border-red-500/40 text-red-300">{label(s.kind)}</span>
                  </div>
                  {s.notes && <div className="text-xs text-zinc-400 italic mt-1 whitespace-pre-wrap">{s.notes}</div>}
                  <div className="text-[10px] font-mono text-zinc-500 mt-1 flex flex-wrap gap-x-3">
                    <span>{fmtDate(s.date)}</span>
                    {s.mileage != null && <span>{fmtNum(s.mileage)} mi</span>}
                    {s.hours != null && <span>{fmtNum(s.hours)} hr</span>}
                    <span>· {s.username}</span>
                  </div>
                  {(parts || labor || total) && (
                    <div className="text-[10px] font-mono mt-1 flex flex-wrap gap-x-3">
                      {parts && <span className="text-zinc-500">Parts <span className="text-zinc-200">{parts}</span></span>}
                      {labor && <span className="text-zinc-500">Labor <span className="text-zinc-200">{labor}</span></span>}
                      {total && <span className="text-red-300">Total {total}</span>}
                    </div>
                  )}
                </div>
                <Link to={`/fleet/${s.unit_id}`} data-testid={`recent-service-history-${s.id}`}
                  className="shrink-0 text-[10px] font-mono uppercase tracking-wider text-red-400 hover:text-red-300 border border-red-500/30 hover:border-red-500/60 px-2 py-1 rounded-sm transition-colors">
                  This Unit →
                </Link>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function AttentionList({ title, items, intervals, accent, empty }) {
  const colors = STATUS_COLORS[accent];
  return (
    <div className="border border-zinc-800 bg-zinc-900 rounded-sm">
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
        <h3 className="text-sm font-semibold">{title}</h3>
        <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border ${colors.badge}`}>{items.length}</span>
      </div>
      <ul className="divide-y divide-zinc-800 max-h-[380px] overflow-y-auto">
        {items.length === 0 && <li className="p-4 text-sm text-zinc-500">{empty}.</li>}
        {items.map((u) => (
          <li key={u.id} className="px-4 py-2.5 text-sm flex items-center justify-between">
            <div>
              <div className="font-medium text-zinc-100 font-mono">{u.unit}</div>
              <div className="text-xs text-zinc-500">{u.desc}</div>
            </div>
            <div className="text-right">
              <div className="text-[11px] font-mono text-zinc-400">{fmtNum(u.mileage)} mi</div>
              <div className="text-[10px] text-zinc-600">{u.type}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
